// API Configuration for External AI Service
// Ensure consistency between External AI and Gallery APIs
//
// Base URL must be the origin only (e.g. https://core.magicfurnish.com), no trailing slash.
// Do NOT include /api — ENDPOINTS already start with /api/v2/...
// REACT_APP_EXTERNAL_AI_BASE_URL takes precedence over REACT_APP_AI_API_URL.

const rawExternalAiBase =
    process.env.REACT_APP_EXTERNAL_AI_BASE_URL || process.env.REACT_APP_AI_API_URL || "";

export function resolveExternalAiBaseUrl() {
    return String(rawExternalAiBase).trim().replace(/\/$/, "");
}

/**
 * Absolute URL for an external AI path (must start with /).
 * Uses the URL constructor so requests stay absolute and axios does not merge with defaults.baseURL.
 */
export function buildExternalAiUrl(path) {
    const base = resolveExternalAiBaseUrl();
    const pathPart = path.startsWith("/") ? path : `/${path}`;
    if (!base) {
        throw new Error(
            "Missing REACT_APP_EXTERNAL_AI_BASE_URL or REACT_APP_AI_API_URL. " +
                "Set it to the API origin only, e.g. https://core.magicfurnish.com (no /api suffix)."
        );
    }
    return new URL(pathPart, base.endsWith("/") ? base : `${base}/`).href;
}

export const API_CONFIG = {
    get BASE_URL() {
        return resolveExternalAiBaseUrl();
    },
    API_KEY: undefined, // No client-side secret
    API_SECRET: undefined, // No client-side secret

    ENDPOINTS: {
        // Upload endpoint - using internal route for staging (no auth required)
        // For external partners, use '/api/v2/ai/external/process' with API key
        UPLOAD: '/api/v2/ai/process',

        // Status endpoint for job queue polling
        STATUS: '/api/v2/ai/status', // Format: /api/v2/ai/status/:jobId

        // Capture-first endpoint for CLIENT-REJECTED uploads. Stores raw bytes
        // only — no AI processing — keyed on the session_id we send. Same host
        // as UPLOAD. Lets partner admins inspect the original file on a failed
        // row even when the file never reached the real /process pipeline.
        CAPTURE: '/api/v2/external-upload/capture',
    },

    POLLING: {
        INTERVAL: 3000, // 3 seconds
        TIMEOUT: 120000, // 2 minutes
        MAX_ATTEMPTS: 40,
    },

    FILE_VALIDATION: {
        // Real-upload validation cap for /process. Anything larger is rejected
        // client-side as FILE_TOO_LARGE before a real upload is attempted.
        MAX_SIZE: 10 * 1024 * 1024,
        // Hard ceiling for the capture-first flow. The capture endpoint accepts
        // any MIME up to 25 MiB and runs no processing, so we still capture
        // client-rejected files (wrong type, 10–25 MiB oversize) the real
        // upload path turns away. Mirrors the endpoint's multer cap; beyond it
        // the server refuses, so we skip capture and just log the failure.
        CAPTURE_MAX_SIZE: 25 * 1024 * 1024,
        ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png'],
        ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png'],
    },
};

export default API_CONFIG;
